import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Subscription } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context, publishFilter } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { from, switchMap } from 'rxjs';
import { BHPayment } from '../entities/BHPayment.js';
import { Participation } from '../entities/Participation.js';
import { ParticipationEinzelberatung } from '../entities/ParticipationEinzelberatung.js';
import { ParticipationWorkshop } from '../entities/ParticipationWorkshop.js';
import { BHFilter } from '../entities/bh-filter.js';
import { Product } from '../entities/products.js';
import { UserStammdaten } from '../entities/stammdaten.js';
import { User } from '../entities/user.js';
import { ParticipationState } from '../enums/enums.js';

@Entity()
export class ParticipationDataviewStateStat extends AdornisEntity {
  static override _class = 'ParticipationDataviewStateStat';

  public static get neededGqlFields(): BaseQLSelectionSet<ParticipationDataviewStateStat> {
    return {
      key: 1,
      ammount: 1,
    };
  }

  @validate(nonOptional())
  @Field(type => String)
  key!: string;

  @validate(nonOptional())
  @Field(type => Int)
  ammount!: number;
}

export type ParticipationStateStatsMappedEnitity = { [k in ParticipationState]: ParticipationDataviewStateStat };
export type ParticipationStateStatsMappedNumber = { [k in ParticipationState]: number };

export const getEmptyParticipationStateStats = () =>
  Object.fromEntries(
    Object.values(ParticipationState).map(key => [key, new ParticipationDataviewStateStat({ key: key, ammount: 0 })]),
  ) as ParticipationStateStatsMappedEnitity;

@Entity()
export class ParticipationDataview extends AdornisEntity {
  static override _class = 'ParticipationDataview';

  static override resolveID(entity: ParticipationDataview): string {
    return entity.participation._id;
  }

  @validate(nonOptional())
  @Field(type => String)
  _id!: string;

  @validate(nonOptional())
  @Field(type => Participation)
  participation!: Participation;

  @Field(type => String) productID: Maybe<string>;

  @Field(type => BHPayment, {
    resolve: function (this: ParticipationDataview) {
      return async () => {
        const c = await getCollection<BHPayment>(BHPayment._class);
        const payment = (await c.findOne({ participationIDs: this.participation._id })) as Maybe<BHPayment>;
        return payment;
      };
    },
  })
  payment: Maybe<BHPayment>;

  @validate(nonOptional())
  @Field(type => User)
  buyer!: User;

  @Field(type => User)
  berater?: User;

  @Subscription(type => [ParticipationDataview])
  static subscribeParticipationWorkshopDataview(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('filter', type => BHFilter) filter: BHFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<ParticipationDataview>) => {
      if (!filter.productID) throw new Error('productID is required');
      let institutionID = '';
      return from(getByID(Product, filter.productID)({ _id: 1, institutionID: 1 })).pipe(
        switchMap(product => {
          if (!product) throw new Error('product not found ' + filter.productID);
          institutionID = product.institutionID;
          return watchQuery(Participation._collectionName, { productID: filter.productID }, { projection: { _id: 1 } });
        }),
        switchMap(async () => {
          const collection = await getRawCollection<ParticipationWorkshop>('participations');
          let pF = await publishFilter(ParticipationWorkshop._class);
          if (context.serverContext) pF = {};
          if (!pF) throw new Error(`Missing publishFilter for ${ParticipationWorkshop._class}`);

          const userLookup = {
            from: User._collectionName,
            localField: 'participation.userID',
            foreignField: '_id',
            as: 'buyer',
          };

          const stammdatenLookup = (() => {
            const matchStammdaten = [
              { $eq: ['$userID', '$$localUserID'] }, //
              { $eq: ['$institutionID', institutionID] },
            ];

            return {
              from: UserStammdaten._collectionName,
              let: {
                localUserID: '$participation.userID',
              },
              pipeline: [
                {
                  $match: {
                    $expr: {
                      $and: matchStammdaten,
                    },
                  },
                },
              ],
              as: 'userStammdaten',
            };
          })();

          const currentPaymentLookup = {
            from: BHPayment._collectionName,
            localField: 'participation._id',
            foreignField: 'participationIDs',
            as: 'payment',
          };

          const sort = filter.getMongoOptions().sort;
          if (!sort['buyer._id']) sort['buyer._id'] = 1; // add _id sort to prevent undifed sort roder when sorting by fields that are not unique. sorting for not unique fields can mess with dataview

          const aggregation = [
            { $match: { $and: [{ _class: ParticipationWorkshop._class }, pF] } },
            { $replaceRoot: { newRoot: { participation: '$$ROOT' } } },

            // for filtering against product
            { $set: { productID: '$participation.productID' } },
            { $lookup: userLookup },
            { $addFields: { buyer: { $arrayElemAt: ['$buyer', 0] }, _class: ParticipationDataview._class } },
            { $lookup: stammdatenLookup },
            { $set: { 'buyer.stammdaten': { $arrayElemAt: ['$userStammdaten', 0] } } },
            { $lookup: currentPaymentLookup },
            { $match: filter.getMongoFilter() },
            { $sort: sort },
            { $skip: skip },
            { $limit: limit },
            { $project: gqlFields },
          ];

          const result = await collection
            .aggregate<ParticipationDataview>(aggregation, { collation: { locale: 'de' } })
            .toArray();
          return result;
        }),
      );
    };
  }

  @Subscription(type => [ParticipationDataview])
  static subscribeParticipationEinzelberatungDataview(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('filter', type => BHFilter) filter: BHFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<ParticipationDataview>) => {
      const productIDFilterAspect = filter.getFilterAspect('productID');
      return watchQuery(Participation._collectionName, productIDFilterAspect?.getMongoFilter() || {}, {}).pipe(
        switchMap(async () => {
          const collection = await getRawCollection<Participation>(Participation._collectionName);
          let pF = await publishFilter(Participation._class);
          if (context.serverContext) pF = {};
          if (!pF) throw new Error(`Missing publishFilter for ${Participation._class}`);
          const result = await collection
            .aggregate<ParticipationDataview>(
              [
                {
                  $match: {
                    $and: [
                      {
                        _class: ParticipationEinzelberatung._class,
                      },
                      pF,
                    ],
                  },
                },
                {
                  $replaceRoot: {
                    newRoot: {
                      participation: {
                        $arrayToObject: {
                          $map: {
                            input: { $objectToArray: '$$ROOT' },
                            as: 'field',
                            in: {
                              k: '$$field.k',
                              v: '$$field.v',
                            },
                          },
                        },
                      },
                    },
                  },
                },
                {
                  $lookup: {
                    from: User._collectionName,
                    localField: 'participation.userID',
                    foreignField: '_id',
                    as: 'buyer',
                  },
                },
                {
                  $addFields: {
                    buyer: { $arrayElemAt: ['$buyer', 0] },
                  },
                },
                {
                  $lookup: {
                    from: Product._collectionName,
                    localField: 'participation.productID',
                    foreignField: '_id',
                    as: 'product',
                  },
                },
                {
                  $addFields: {
                    product: { $arrayElemAt: ['$product', 0] },
                  },
                },
                {
                  $addFields: {
                    slot: {
                      $filter: {
                        input: '$product.slots',
                        as: 'slot',
                        cond: {
                          $eq: ['$$slot._id', '$participation.timeslotID'],
                        },
                      },
                    },
                  },
                },
                {
                  $addFields: {
                    slot: { $arrayElemAt: ['$slot', 0] },
                  },
                },
                {
                  $lookup: {
                    from: User._collectionName,
                    localField: 'slot.beraterID',
                    foreignField: '_id',
                    as: 'berater',
                  },
                },
                {
                  $addFields: {
                    berater: { $arrayElemAt: ['$berater', 0] },
                  },
                },
                {
                  $addFields: {
                    _class: ParticipationDataview._class,
                  },
                },
                // for filtering against product
                { $set: { productID: '$participation.productID' } },
                { $match: filter.getMongoFilter() },
                { $project: gqlFields },
                { $sort: filter.getMongoOptions().sort },
                { $skip: skip },
                { $limit: limit },
              ],
              { collation: { locale: 'de' } },
            )
            .toArray();

          return result;
        }),
      );
    };
  }

  @Subscription(type => [ParticipationDataviewStateStat])
  static subscribeParticipationsStatsByProductID(@Arg('productID', type => String) productID: string) {
    return (gqlFields: BaseQLSelectionSet<ParticipationDataview>) => {
      const findRecord = {
        productID: productID ?? 'NO_PRODUCT',
      };

      return getCollectionHandle(Participation._collectionName)
        .watchQuery(findRecord, { projection: { _id: 1, _class: 1, state: 1 } })
        .pipe(
          switchMap(async () => {
            const collection = await getRawCollection<Participation>(Participation._collectionName);
            let pF = await publishFilter(Participation._class);
            if (context.serverContext) pF = {};
            if (!pF) throw new Error(`Missing publishFilter for ${Participation._class}`);

            const allStatesData = await await collection
              .aggregate<{ _id: string; count: number }>([
                {
                  $match: findRecord,
                },
                {
                  $group: {
                    _id: '$state',
                    count: { $sum: 1 },
                  },
                },
              ])
              .toArray();

            const mappedStats = allStatesData.reduce((stats, curState) => {
              const curStateKey = curState._id;

              if (curState && stats[curStateKey]) {
                stats[curStateKey].ammount = curState.count;
              }

              return stats;
            }, getEmptyParticipationStateStats());

            return Object.values(mappedStats);
          }),
        );
    };
  }
}

export const subscribeParticipationsStatsByProductID = registerSubscription({
  operationName: 'subscribeParticipationsStatsByProductID',
  type: () => [ParticipationDataviewStateStat],
  params: [{ name: 'productID', type: () => String }],

  resolve: (productID: string) => (gqlFields: BaseQLSelectionSet<ParticipationDataview>) => {
    const findRecord = {
      productID: productID ?? 'NO_PRODUCT',
    };

    return watchQuery(Participation._collectionName, findRecord, { projection: { _id: 1, _class: 1, state: 1 } }).pipe(
      switchMap(async () => {
        const collection = await getRawCollection<Participation>(Participation._collectionName);
        let pF = await publishFilter(Participation._class);
        if (context.serverContext) pF = {};
        if (!pF) throw new Error(`Missing publishFilter for ${Participation._class}`);

        const allStatesData = await await collection
          .aggregate<{ _id: string; count: number }>([
            {
              $match: findRecord,
            },
            {
              $group: {
                _id: '$state',
                count: { $sum: 1 },
              },
            },
          ])
          .toArray();

        const mappedStats = allStatesData.reduce((stats, curState) => {
          const curStateKey = curState._id;

          if (curState && stats[curStateKey]) {
            stats[curStateKey].ammount = curState.count;
          }

          return stats;
        }, getEmptyParticipationStateStats());

        return Object.values(mappedStats);
      }),
    );
  },
});
